var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
/* eslint-disable max-lines-per-function */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useImperativeHandle } from 'react';
import CBForm from '@cb/apricot/CBForm';
import { Glyph, glyphNames } from '@cb/apricot-react-glyph';
import { Icon, iconNames } from '@cb/apricot-react-icon';
import { SquareButton } from '@cb/apricot-react-button';
import cn from 'classnames';
import { useUID } from 'react-uid';
import { ClearButton } from './Input';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isIcon = (x) => iconNames.includes(x);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isGlyph = (x) => glyphNames.includes(x);
// ref is for the wrapping div
export const InputDropdown = React.forwardRef((_a, ref) => {
    var { labelId, inputId, dropdownId, validationMessageId, ariaDescribedby, type = 'text', label, ariaLabel, className, labelClassName, name, floating = true, floatingBg, condensed = false, disabled = false, clearable = true, required, icon, iconRight = false, dropdownOverlay = false, placeholder, validation, validationMsg, helperClassName, ariaLive, inputAutocomplete = 'off', scrollLimit = 0, dataItems = [], defaultItems = false, itemsText = ['suggestion available', 'suggestions available'], noItemsText = 'no suggestions available', button, buttonOffScreen, onChange = () => { }, onFocus = () => { }, onClick = () => { }, onClear = () => { }, onBlur = () => { }, onKeyUp = () => { }, onKeyDown = () => { }, callBack = () => { }, onBuild = null, defaultValue, value, event, dropdownIcon = false, dropdownIconLeft = false, escClose = true, closeOnClickOutside = true, closeOnSelect = true, cbRef } = _a, props = __rest(_a, ["labelId", "inputId", "dropdownId", "validationMessageId", "ariaDescribedby", "type", "label", "ariaLabel", "className", "labelClassName", "name", "floating", "floatingBg", "condensed", "disabled", "clearable", "required", "icon", "iconRight", "dropdownOverlay", "placeholder", "validation", "validationMsg", "helperClassName", "ariaLive", "inputAutocomplete", "scrollLimit", "dataItems", "defaultItems", "itemsText", "noItemsText", "button", "buttonOffScreen", "onChange", "onFocus", "onClick", "onClear", "onBlur", "onKeyUp", "onKeyDown", "callBack", "onBuild", "defaultValue", "value", "event", "dropdownIcon", "dropdownIconLeft", "escClose", "closeOnClickOutside", "closeOnSelect", "cbRef"]);
    const inputDropdownRef = useRef(null);
    const inputRef = useRef(null);
    const uid = useUID();
    // eslint-disable-next-line no-param-reassign
    inputId = inputId || `apricot_input_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    dropdownId = dropdownId || `apricot_dropdown_${uid}`;
    // eslint-disable-next-line no-param-reassign
    labelId = labelId || `apricot_label_${uid}`;
    // eslint-disable-next-line no-param-reassign
    validationMessageId = validationMessageId || `apricot_input_err_${uid}`;
    useImperativeHandle(ref, () => inputDropdownRef.current);
    useEffect(() => {
        const inputDropdownPlugin = CBForm.inputDropdown({
            elem: inputDropdownRef.current,
            event,
            callBack,
            onBuild,
            clearInput: clearable,
            icon: dropdownIcon,
            iconLeft: dropdownIconLeft,
            escClose,
            closeOnSelect,
            closeOnClickOutside,
            scrollLimit,
            dataItems,
            defaultItems,
            itemsText,
            noItemsText,
        });
        floating && CBForm.floatingLabel(inputRef.current);
        clearable && CBForm.clearInput({ elem: inputRef.current });
        if (cbRef) {
            // eslint-disable-next-line no-param-reassign
            cbRef.current = inputDropdownPlugin;
        }
        return () => {
            inputDropdownPlugin && inputDropdownPlugin.destroy();
        };
    }, [disabled]);
    const mainClassName = cn('cb-input', 'cb-input-dropdown', dropdownOverlay && 'cb-input-dropdown-overlay', (!label || (!!label && !floating)) && 'cb-input-no-label', (clearable || button) && 'cb-btn-input', floating && label && 'cb-floating-label', clearable && 'cb-clear-input', condensed && 'cb-input-condensed', disabled && 'cb-disabled', validation && `cb-validation-${validation}`, validation && 'display-block', className);
    const wrapperClassName = cn(icon && `cb-input-icon-${iconRight ? 'right' : 'left'}`);
    const labelClass = cn(required && 'cb-required', floating && floatingBg && `cb-${floatingBg}-bg`, disabled && 'cb-text-disabled', labelClassName);
    const helperClass = cn('cb-input-helper', validation && `cb-validation-${validation}`, helperClassName);
    const getDescribedBy = () => {
        if (validation) {
            if (validationMsg && ariaDescribedby) {
                return `${ariaDescribedby} ${validationMessageId}`;
            }
            return validationMsg ? validationMessageId : ariaDescribedby;
        }
        return ariaDescribedby;
    };
    const describedbyValue = getDescribedBy() || null;
    return (React.createElement(React.Fragment, null,
        !!label && !floating && (React.createElement("label", { htmlFor: inputId, id: labelId, className: labelClass }, label)),
        React.createElement("div", { className: mainClassName, ref: inputDropdownRef, "data-cbtrack-comp": "apricot-react:input-dropdown" },
            React.createElement("div", { className: wrapperClassName },
                !!label && floating && (React.createElement("label", { htmlFor: inputId, id: labelId, className: labelClass }, label)),
                React.createElement("input", Object.assign({ type: type, name: name, autoComplete: inputAutocomplete, ref: inputRef, id: inputId, "aria-label": ariaLabel, "aria-describedby": describedbyValue || undefined, "aria-autocomplete": "list", "aria-controls": dropdownId, "aria-required": required, defaultValue: defaultValue, value: value, role: "combobox", "aria-expanded": "false", "aria-haspopup": "listbox", disabled: disabled, placeholder: placeholder, onChange: onChange, onFocus: onFocus, onBlur: onBlur, onKeyUp: onKeyUp, onKeyDown: onKeyDown }, props)),
                icon && isIcon(icon) && React.createElement(Icon, { name: icon, decorative: true }),
                icon && !isIcon(icon) && isGlyph(icon) && React.createElement(Glyph, { name: icon, decorative: true }),
                clearable && !button && React.createElement(ClearButton, { inputId: inputId, onClear: onClear }),
                button && (React.createElement(SquareButton, { onClick: onClick, greyscale: true, icon: button, label: buttonOffScreen || '', "aria-controls": inputId }))),
            React.createElement("div", { className: "cb-dropdown-input", "data-cb-input": "dropdown-input" },
                React.createElement("span", { role: "status", className: "cb-dropdown-input-items sr-only" }),
                React.createElement("div", { className: "cb-dropdown-menu" },
                    React.createElement("ul", { id: dropdownId, role: "listbox", "aria-labelledby": label ? labelId : undefined })))),
        validation && validationMsg && (React.createElement("p", { id: validationMessageId, className: helperClass, "aria-live": ariaLive }, validationMsg))));
});
//# sourceMappingURL=InputDropDown.js.map